import React from 'react';
import { SEO } from '@/components/SEO';
import ServiceSchema from '@/components/services/ServiceSchema';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ServiceHero from '@/components/services/ServiceHero';
import ServiceBenefits from '@/components/services/ServiceBenefits';
import ServiceFAQ from '@/components/services/ServiceFAQ';
import ServiceCTA from '@/components/services/ServiceCTA';
import ServiceCrossLinks from '@/components/services/ServiceCrossLinks';
import { Sun } from 'lucide-react';
import solarCleaning1 from '@/assets/solar-cleaning-ground-pole.webp';
import solarCleaning2 from '@/assets/solar-cleaning-2.webp';

const SolarPanelCleaningPage = () => {
  const serviceData = {
    title: "Professional Solar Panel Cleaning in Ventura, CA",
    subtitle: "Maximize Solar Efficiency with Expert Panel Maintenance",
    description: "Boost your solar energy production with our specialized solar panel cleaning services. Our eco-friendly methods remove dust, bird droppings, and debris that reduce efficiency in Ventura County's sunny climate.",
    icon: <Sun className="h-16 w-16 text-ventura-blue" />,
    beforeImage: solarCleaning1,
    afterImage: solarCleaning2
  };

  const benefits = [
    "Increases energy production by up to 30%",
    "Extends solar panel lifespan",
    "Eco-friendly cleaning methods",
    "Professional safety protocols",
    "Regular maintenance programs available",
    "Maximizes return on solar investment"
  ];

  const faqs = [
    {
      question: "How often should solar panels be cleaned in Ventura County?",
      answer: "Due to Ventura's dusty conditions and bird activity, we recommend cleaning solar panels every 3-6 months to maintain optimal efficiency and energy production."
    },
    {
      question: "Will dirty solar panels really affect my energy savings?",
      answer: "Yes! Studies show that dirty solar panels can lose 15-30% efficiency. In Ventura's sunny climate, regular cleaning ensures you're maximizing your solar investment."
    },
    {
      question: "Is it safe to clean solar panels myself?",
      answer: "We strongly recommend professional cleaning for safety and warranty protection. Our team uses proper equipment and techniques specifically designed for solar panels throughout Ventura County."
    }
  ];

  return (
    <>
      <SEO
        title="Solar Panel Cleaning Ventura CA | Professional Solar Maintenance"
        description="Expert solar panel cleaning Ventura & Oxnard CA. Increase energy efficiency 30% with eco-friendly methods in Camarillo, Thousand Oaks. Free quotes!"
        keywords="solar panel cleaning Ventura CA, solar maintenance Camarillo, panel washing Thousand Oaks, Ventura County solar services"
        path="/services/solar-panel-cleaning"
      />
      <ServiceSchema
        serviceName="Solar Panel Cleaning"
        description="Expert solar panel cleaning Ventura & Oxnard CA. Increase energy efficiency 30% with eco-friendly methods in Camarillo, Thousand Oaks."
        serviceType="Solar Panel Cleaning"
        areaServed={["Ventura", "Oxnard", "Camarillo", "Thousand Oaks", "Simi Valley", "Fillmore", "Moorpark", "Port Hueneme", "Santa Paula"]}
        additionalServices={["Window Cleaning", "Pressure Washing", "Gutter Cleaning"]}
      />
      
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <ServiceHero {...serviceData} />
        
        <section className="section-padding bg-white">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">How Much Efficiency Do Dirty Solar Panels Lose in Ventura County?</h2>
              <p className="text-lg text-gray-600 mb-8 text-center">
                Dirty solar panels can lose 15-30% efficiency, and Ventura County's dust, pollen, and bird droppings build up faster than in
                many inland areas. We recommend cleaning every 3-6 months to keep your system at peak performance, maximizing the energy
                savings and return on your solar investment.
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Specialized Solar Panel Services</h3>
                  <p className="text-gray-600 mb-4">
                    Expert solar maintenance for your Ventura County installation:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Gentle, effective panel surface cleaning</li>
                    <li>Inverter and mounting system inspection</li>
                    <li>Bird deterrent installation</li>
                    <li>Performance monitoring consultation</li>
                    <li>Residential and commercial systems</li>
                    <li>Scheduled maintenance programs</li>
                  </ul>
                </div>
                <div className="before-after-container">
                  <div className="before-after-image">
                    <img 
                      src={serviceData.beforeImage} 
                      alt="Dirty solar panels before professional cleaning in Ventura CA" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label before-label">Before</span>
                  </div>
                  <div className="before-after-image">
                    <img 
                      src={serviceData.afterImage} 
                      alt="Clean solar panels after professional maintenance service in Ventura County CA" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label after-label">After</span>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Maximizing Solar Performance in Ventura County's Climate</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Dust and Pollen Management</h4>
                    <p className="text-gray-600 mb-4">
                      Ventura County's agricultural areas like Fillmore and Santa Paula generate significant dust and pollen that settles on solar panels. Our specialized cleaning removes these fine particles that can reduce efficiency by 15-25%, ensuring maximum energy production from your investment.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Coastal Salt Contamination</h4>
                    <p className="text-gray-600 mb-4">
                      Properties near the coast in Oxnard, Port Hueneme, and Ventura face salt air contamination that creates stubborn films on solar panels. Our gentle cleaning process removes salt deposits without damaging sensitive panel surfaces or protective coatings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Professional Solar Panel Maintenance Techniques</h3>
                <p className="text-gray-600 mb-4">
                  Solar panel cleaning requires specialized knowledge and equipment to avoid damage while achieving optimal results. Our technicians are trained in proper panel handling, understand different panel types, and use only approved cleaning solutions that won't void manufacturer warranties.
                </p>
                <p className="text-gray-600 mb-4">
                  We use deionized water systems that leave no mineral deposits, ensuring panels dry completely clean without water spots. This is particularly important in Ventura County where hard water minerals can create persistent films that reduce solar efficiency even after cleaning.
                </p>
                <p className="text-gray-600 mb-6">
                  Our cleaning process includes inspection of mounting systems, wiring, and inverters to identify potential issues before they affect performance. Early detection of problems like loose connections or damaged panels can prevent costly repairs and maintain system efficiency throughout its lifespan.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-12">
                <h3 className="text-2xl font-semibold mb-4">Bird Management and Panel Protection</h3>
                <p className="text-gray-600 mb-4">
                  Ventura County's diverse bird population, including seagulls near the coast and various inland species, can create significant soiling issues for solar installations. Bird droppings are particularly problematic because they can create hot spots that damage panels and dramatically reduce efficiency.
                </p>
                <p className="text-gray-600">
                  We offer bird deterrent installation and ongoing management services to protect your solar investment. Our humane bird control methods prevent nesting under panels while maintaining the aesthetic appeal of your roof installation. Regular maintenance combined with effective bird management ensures consistent solar performance year-round.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-4">ROI and Environmental Impact</h3>
                <p className="text-gray-600 mb-4">
                  Regular professional solar panel cleaning in Ventura County typically pays for itself through increased energy production. Studies show that dirty panels can lose 20-30% efficiency, meaning a modest cleaning investment can yield significant returns through improved power generation.
                </p>
                <p className="text-gray-600">
                  Clean solar panels also contribute more effectively to California's renewable energy goals and reduce your carbon footprint. By maintaining peak efficiency, you're maximizing the environmental benefits of your solar installation while protecting your financial investment in clean energy technology.
                </p>
              </div>
            </div>
          </div>
        </section>

        <ServiceBenefits benefits={benefits} />
        <ServiceCrossLinks 
          currentService="Solar Panel Cleaning"
          relatedServices={[
            { name: 'Window Cleaning', path: '/services/window-washing', icon: 'window' },
            { name: 'Pressure Washing', path: '/services/pressure-washing', icon: 'pressure' },
            { name: 'Gutter Cleaning', path: '/services/gutter-cleaning', icon: 'gutter' },
          ]}
        />
        <ServiceFAQ faqs={faqs} serviceName="Solar Panel Cleaning" />
        <ServiceCTA 
          title="Ready to Maximize Your Solar Efficiency?"
          description="Schedule your professional solar panel cleaning and start generating more clean energy for your Ventura County property."
        />
        <Footer />
      </div>
    </>
  );
};

export default SolarPanelCleaningPage;